<template>
  <div
    v-show="showKeyboard"
    style="position: fixed;  z-index: 1000;"
    :style="
      $vuetify.breakpoint.smAndDown
        ? 'width: 100%'
        : 'left: 50%; transform: translate(-50%, 0); width: 60%; margin: 20px'
    "
    ref="keyboardWrapper"
  >
    <div :class="keyboardClass"></div>
    <v-btn
      fab
      icon
      small
      elevation="5"
      style="position: absolute; top: -42px; background-color: #ececec"
      :style="$vuetify.breakpoint.smAndDown ? 'right: 0' : 'right: -34px'"
      @click.prevent="showKeyboard = false"
      ><v-icon>mdi-close</v-icon></v-btn
    >
  </div>
</template>

<script>
import Keyboard from 'simple-keyboard';
import 'simple-keyboard/build/css/index.css';
import sleep from '@/util/sleep';
import { mapState } from 'vuex';

export default {
  name: 'OnScreenKeyboard',

  props: {
    keyboardClass: {
      type: String,
      default: 'simple-keyboard',
    },
  },

  data() {
    return {
      showKeyboard: false,
      keyboard: null,
      selectedInput: null,
      timeoutId: null,
      haltEventListeners: false,
      rootAppElement: null, // element that vuetify adds click outside listeners on
      haltedEventListeners: [], // event listeners that cause vuetify autocompletes to close on click
    };
  },

  computed: {
    ...mapState('settings', ['isPrintTerminal']),

    isNumberInputSelected() {
      if (!this.selectedInput) {
        return false;
      }
      return (
        this.selectedInput.parentElement?.parentElement?.parentElement?.parentElement?.classList.contains(
          'js-number-input',
        ) || false
      );
    },
  },

  mounted() {
    // Halt adding vuetify event listeners that cause autocompletes to close on outside click.
    // They are re-added in onInputBlur method
    this.rootAppElement = document.querySelector('[data-app]');
    this.rootAppElement.realAddEventListener = this.rootAppElement.addEventListener;
    this.rootAppElement.addEventListener = (eventName, callback, options) => {
      if (!this.haltEventListeners) {
        this.rootAppElement.realAddEventListener(eventName, callback, options);
      } else {
        this.haltedEventListeners.push({
          eventName,
          callback,
          options,
        });
      }
    };

    window.addEventListener('pointerdown', this.onPointerDown);
  },

  beforeDestroy() {
    window.removeEventListener('pointerdown', this.onPointerDown);
    this.rootAppElement.addEventListener = this.rootAppElement.realAddEventListener;
  },

  methods: {
    async onPointerDown(event) {
      // Using this method instead of adding listeners to all inputs,
      // because inputs might be mounted later than this component.
      let input = null;
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
        input = event.target;
      } else if (event.target.getAttribute('aria-label') === 'clear icon') {
        // clear icon was clicked inside vuetify input with clearable=true
        // we handle this, because the input is focused after clear icon click
        input = event.target.parentElement.parentElement.parentElement.querySelector('input');
        if (input.type === 'hidden' || input.readOnly) {
          input = null; // clicked on v-select clear icon
        }
      }
      if (!input) {
        // clicked element is not an input and is not related to any input
        return;
      }

      this.selectedInput = input;
      if (this.isNumberInputSelected) {
        this.setupNumberKeyboard();
      } else {
        this.setupFullKeyboard();
      }
      this.setKeyboardLocation(event);
      this.showKeyboard = true;
      this.haltEventListeners = true;
      await sleep(100); // gives time for previously focused input's blur event to resolve
      clearTimeout(this.timeoutId);
      this.keyboard.setInput(input.value);
      this.selectedInput.addEventListener('blur', this.onInputBlur);
    },

    setupFullKeyboard() {
      if (this.keyboard) {
        this.keyboard.destroy();
      }
      this.keyboard = new Keyboard(this.keyboardClass, {
        onChange: this.onChange,
        onKeyPress: this.onKeyPress,
        newLineOnEnter: true,
        preventMouseDownDefault: true, // prevents focus from going away from regular inputs
        // preventMouseUpDefault: true, // I think these are not necessary
        // stopMouseDownPropagation: true,
        // stopMouseUpPropagation: true,
        layout: {
          default: [
            'ą č ę ė į š ų ū ž',
            '` 1 2 3 4 5 6 7 8 9 0 - = {bksp}',
            '{tab} q w e r t y u i o p [ ] \\',
            "{lock} a s d f g h j k l ; ' {enter}",
            '{shift} z x c v b n m , . / {shift}',
            '.com @ {space}',
          ],
          shift: [
            'Ą Č Ę Ė Į Š Ų Ū Ž',
            '~ ! @ # $ % ^ & * ( ) _ + {bksp}',
            '{tab} Q W E R T Y U I O P { } |',
            '{lock} A S D F G H J K L : " {enter}',
            '{shift} Z X C V B N M < > ? {shift}',
            '.com @ {space}',
          ],
        },
      });
    },

    setupNumberKeyboard() {
      if (this.keyboard) {
        this.keyboard.destroy();
      }
      this.keyboard = new Keyboard(this.keyboardClass, {
        onChange: this.onChange,
        onKeyPress: this.onKeyPress,
        newLineOnEnter: true,
        preventMouseDownDefault: true, // prevents focus from going away from regular inputs
        // preventMouseUpDefault: true, // I think these are not necessary
        // stopMouseDownPropagation: true,
        // stopMouseUpPropagation: true,
        layout: {
          default: ['7 8 9', '4 5 6', '1 2 3', '0 .', '{bksp}'],
          shift: [],
        },
      });
    },

    onInputBlur() {
      // re-adds halted vuetify event listeners
      for (let i = 0; i < this.haltedEventListeners.length; i++) {
        const { eventName, callback, options } = this.haltedEventListeners[i];
        this.rootAppElement.realAddEventListener(eventName, callback, options);
      }
      this.haltedEventListeners = [];

      clearTimeout(this.timeoutId);
      this.timeoutId = setTimeout(() => {
        this.selectedInput.removeEventListener('blur', this.onInputBlur);
        this.showKeyboard = false;
        this.haltEventListeners = false;
      }, 200); // delay to exclude blurs caused by autocomplete search query inputs
    },

    async onChange(value) {
      if (!this.selectedInput) {
        return;
      }
      this.selectedInput.value = value;
      // Manually dispatching events so vue event listeners like @input are triggered
      this.selectedInput.dispatchEvent(new Event('input'));
      this.selectedInput.dispatchEvent(new Event('change'));
      this.selectedInput.dispatchEvent(new Event('keyup'));
      this.selectedInput.dispatchEvent(new Event('keydown'));
    },

    onKeyPress(button) {
      this.$emit('onKeyPress', button);

      // If you want to handle the shift and caps lock buttons
      if (button === '{shift}' || button === '{lock}') {
        this.handleShift();
      }
    },

    handleShift() {
      const currentLayout = this.keyboard.options.layoutName;
      const shiftToggle = currentLayout === 'default' ? 'shift' : 'default';

      this.keyboard.setOptions({
        layoutName: shiftToggle,
      });
    },

    setKeyboardLocation(event) {
      const screenMiddle = window.innerHeight / 2;
      const clickedHeight = event.clientY;

      // if input is below middle of the screen show keyboard on top and vice versa
      if (clickedHeight > screenMiddle) {
        this.$refs.keyboardWrapper.style.top = this.$vuetify.breakpoint.smAndDown ? '44px' : '24px';
        this.$refs.keyboardWrapper.style.bottom = 'auto';
      } else {
        this.$refs.keyboardWrapper.style.bottom = this.$vuetify.breakpoint.smAndDown ? '0' : '10px';
        this.$refs.keyboardWrapper.style.top = 'auto';
      }
    },
  },
};
</script>

<style scoped></style>
